import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AuthCredentials,
  AuthResponse,
  User,
  UserProfileRequestDTO,
  UsersResponse,
  UserResponseDTO,
  AddManualStudentPayload,
} from './models';
import { environment } from '../../../../../environments/environment';
@Injectable({
  providedIn: 'root',
})
export class UserService {
  private http = inject(HttpClient);

  private baseUrl = environment.host;

  getAll(
    page = 0,
    limit = 10,
    filters?: {
      search?: string;
      role?: string;
      status?: string;
      batchId?: string;
    },
  ) {
    let url = this.baseUrl + '/api/v1/user?page=' + page + '&limit=' + limit;
    if (filters?.search) url += '&search=' + encodeURIComponent(filters.search);
    if (filters?.role) url += '&role=' + encodeURIComponent(filters.role);
    if (filters?.status) url += '&status=' + encodeURIComponent(filters.status);
    if (filters?.batchId)
      url += '&batchId=' + encodeURIComponent(filters.batchId);
    return this.http.get<UsersResponse>(url);
  }

  getUserInfo() {
    return this.http.get<UserResponseDTO>(this.baseUrl + '/api/v1/user/me');
  }

  login(credentials: AuthCredentials) {
    return this.http.post<AuthResponse>(
      this.baseUrl + '/api/v1/auth',
      credentials,
    );
  }

  register(user: User) {
    return this.http.post<Partial<User>>(this.baseUrl + '/api/v1/user', user);
  }

  // Registration v2 (three-step flow)
  registerIdentify(username: string) {
    return this.http.post<{ success: boolean; message: string }>(
      this.baseUrl + '/api/v1/auth/register/identify',
      {
        username,
      },
    );
  }

  registerVerify(username: string, code: string) {
    return this.http.post<{ registrationToken: string }>(
      this.baseUrl + '/api/v1/auth/register/verify',
      {
        username,
        code,
      },
    );
  }

  registerComplete(payload: {
    token: string;
    password: string;
    firstName: string;
    lastName: string;
    role: string;
    dateOfBirth?: Date;
    parentEmail?: string;
    inviteToken?: string;
  }) {
    return this.http.post<{
      token?: string;
      refreshToken?: string;
      user?: UserResponseDTO;
      message?: string;
    }>(this.baseUrl + '/api/v1/auth/register/complete', payload);
  }

  submitParentalConsent(token: string, action: string) {
    return this.http.post<{ message: string }>(
      this.baseUrl + '/api/v1/auth/parent-consent',
      { token, action },
    );
  }

  sendVerificationCode(username?: string) {
    let endpoint = this.baseUrl + '/api/v1/user/send-verification-code';
    let payload = {};

    if (username) {
      endpoint = this.baseUrl + '/api/v1/user/send-verification-code-noauth';
      payload = { username };
    }

    return this.http.post<any>(endpoint, payload);
  }

  verifyUserWithOtp(code: string) {
    return this.http.post<any>(this.baseUrl + '/api/v1/user/verify-user', {
      code,
    });
  }

  requestPasswordResetTokenWithOtp(username: string, code: string) {
    return this.http.post<{ resetToken: string }>(
      this.baseUrl + '/api/v1/user/reset-password-request',
      {
        code,
        user: username,
      },
    );
  }

  resetPassword(token: string, password: string) {
    return this.http.put<any>(this.baseUrl + '/api/v1/user/reset-password', {
      token,
      password,
    });
  }

  updateUserProfile(userProfile: UserProfileRequestDTO) {
    return this.http.put<UserResponseDTO>(
      this.baseUrl + '/api/v1/user/update-profile',
      userProfile,
    );
  }

  getZoomAuthToken(code: string, redirectUri: string) {
    return this.http.post<any>(this.baseUrl + '/api/v1/auth/zoom-auth', {
      code,
      redirectUri,
    });
  }

  linkZoomAccount(accessToken: string, refreshToken?: string) {
    return this.http.post<any>(this.baseUrl + '/api/v1/auth/zoom-link', {
      accessToken,
      refreshToken,
    });
  }

  linkZoomAccountByEmail(userId: string, zoomEmail: string) {
    return this.http.post<unknown>(
      this.baseUrl + '/api/v1/user/zoom-link-by-email',
      {
        userId,
        zoomEmail,
      },
    );
  }

  unlinkZoomAccountById(userId: string) {
    return this.http.delete<unknown>(
      this.baseUrl + '/api/v1/user/zoom-unlink',
      {
        body: { userId },
      },
    );
  }

  blockUser(userId: string) {
    return this.http.put<unknown>(
      `${this.baseUrl}/api/v1/user/${userId}/status`,
      { status: 'BLOCKED' },
    );
  }

  unblockUser(userId: string) {
    return this.http.put<unknown>(
      `${this.baseUrl}/api/v1/user/${userId}/status`,
      { status: 'VERIFIED' },
    );
  }

  changeRole(userId: string, role: string) {
    return this.http.put<unknown>(
      `${this.baseUrl}/api/v1/user/${userId}/role`,
      { role },
    );
  }

  requestImpersonation(userId: string) {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.baseUrl}/api/v1/admin/impersonate/request`,
      { userId },
    );
  }

  verifyImpersonation(userId: string, otp: string) {
    return this.http.post<{ token: string; user: UserResponseDTO }>(
      `${this.baseUrl}/api/v1/admin/impersonate/verify`,
      { userId, otp },
    );
  }

  unlinkAccount(provider: string, userId: string) {
    return this.http.delete<unknown>(
      this.baseUrl + '/api/v1/auth/unlink-account',
      {
        body: {
          provider,
          userId,
        },
      },
    );
  }

  refreshToken(refreshToken: string) {
    return this.http.post<{ token: string; refreshToken: string }>(
      this.baseUrl + '/api/v1/auth/refresh',
      {
        refreshToken,
      },
    );
  }

  getPublicTutors(
    page = 1,
    limit = 10,
    filters?: {
      search?: string;
      subject?: string;
      medium?: string;
      classFormat?: string;
      district?: string;
    },
  ) {
    let url =
      this.baseUrl + '/api/v1/user/tutors?page=' + page + '&limit=' + limit;
    if (filters?.search) url += '&search=' + encodeURIComponent(filters.search);
    if (filters?.subject)
      url += '&subject=' + encodeURIComponent(filters.subject);
    if (filters?.medium) url += '&medium=' + encodeURIComponent(filters.medium);
    if (filters?.classFormat)
      url += '&classFormat=' + encodeURIComponent(filters.classFormat);
    if (filters?.district)
      url += '&district=' + encodeURIComponent(filters.district);
    return this.http.get<any>(url);
  }

  getPublicTutorProfile(identifier: string) {
    return this.http.get<{
      tutor: UserResponseDTO;
      courses: any[];
      liveClasses: any[];
    }>(this.baseUrl + '/api/v1/user/tutors/' + encodeURIComponent(identifier));
  }

  validateSlug(slug: string) {
    return this.http.get<{
      unique: boolean;
      slug: string;
      suggestion?: string;
    }>(
      this.baseUrl +
        '/api/v1/user/validate-slug?slug=' +
        encodeURIComponent(slug),
    );
  }

  updateTutorProfile(tutorInfo: any) {
    return this.http.put<UserResponseDTO>(
      this.baseUrl + '/api/v1/user/me/tutor-profile',
      tutorInfo,
    );
  }

  getPendingTutorProfiles() {
    return this.http.get<any[]>(
      this.baseUrl + '/api/v1/user/pending-tutor-profiles',
    );
  }

  approveTutorProfile(userId: string) {
    return this.http.post<UserResponseDTO>(
      `${this.baseUrl}/api/v1/user/${userId}/approve-tutor-profile`,
      {},
    );
  }

  rejectTutorProfile(userId: string) {
    return this.http.post<UserResponseDTO>(
      `${this.baseUrl}/api/v1/user/${userId}/reject-tutor-profile`,
      {},
    );
  }

  deleteAccount() {
    return this.http.delete<{ success: boolean; message: string }>(
      this.baseUrl + '/api/v1/user/me',
    );
  }

  addManualStudent(payload: AddManualStudentPayload) {
    return this.http.post<{ success: boolean; message: string; user: any }>(
      `${this.baseUrl}/api/v1/user/add-manual-student`,
      payload,
    );
  }

  adminResetPassword(userId: string, password: string) {
    return this.http.put<{ success: boolean; message: string }>(
      `${this.baseUrl}/api/v1/user/${userId}/admin-reset-password`,
      { password },
    );
  }
}
